import React from 'react';
import { RGBColor } from 'react-color';
import { connect } from 'react-redux';

import { projectApi } from 'container/projectApi';
import { orange } from '@mui/material/colors';
import { AdminRoute } from 'app/route/admin';
import { CreateProjectExistingCompany } from 'component/CreateProject/CreateProjectExistingCompany';
import { CreateProjectNewCompany } from 'component/CreateProject/CreateProjectNewCompany';
import { AutocompleteData } from 'component/Form/Autocomplete';
import { CompanyModel } from 'api/dto/Company.dto';
import { EnumModel } from 'api/dto/Enum.dto';
import { ProjectKind } from 'model/ProjectKind';
import { ProjectType } from 'model/ProjectType';
import { DEFAULT_TIMEZONE } from 'api/dto/Timezone.dto';
import { compose } from 'redux';
import { formValueSelector, reduxForm, InjectedFormProps } from 'redux-form';
import { AdminDispatch, AdminState } from 'reduxStore/appStore';
import { selectCompanyList, selectCompanyListResource } from 'reduxStore/company/selectors';
import {
  selectBenchmarkSectors,
  selectEmployeeRangeGroup,
  selectTimezoneAutocompleteOptions,
} from 'reduxStore/configs/selectors';
import { openSnackbar } from 'reduxStore/snackbars/asyncActions';
import { AdminFormNames } from 'register/AdminFormNames';
import { pushRoute } from 'router/routerAction';
import tinycolor from 'tinycolor2';
import { CircleModel } from 'api/dto/Circle.dto';
import { selectCircles, selectCirclesResource } from 'reduxStore/circles/selectors';
import { Resource } from 'model/Resource';
import { ResourceLoader } from 'component/ResourceLoaderNew/ResourceLoader';

export namespace CreateProjectForm {
  export type StateProps = {
    projectType: number;
    listResource: Resource<CompanyModel[]>;
    circles: CircleModel[];
    companyList: CompanyModel[];
    benchmarkSectors: EnumModel[];
    circlesResource: Resource<CircleModel[]>;
    employeeRangeGroup: EnumModel[];
    timezones: AutocompleteData<string>[];
    isEngagementScoreProjectType: boolean;
    onSubmit: (data: FormData, dispatch: AdminDispatch, props: Props) => void;
    initialValues: FormData;
  };
  export type DispatchProps = {};
  export type OwnProps = {
    onSubmitSuccess?: () => void;
  };
  export type FormData = {
    circle?: string;
    companyName?: string;
    companyLogo?: File;
    company?: CompanyModel;
    brandColor?: RGBColor;
    timezone: string;
    benchmarkSector?: number;
    approxNumberEmployee?: number;
    kind?: ProjectKind;
  };
  export type Props = OwnProps & InjectedFormProps<FormData, OwnProps> & StateProps;
}

export const CreateProjectFormPure: React.FC<CreateProjectForm.Props> = (props) => (
  <ResourceLoader resource={[props.circlesResource, props.listResource]}>
    {() => (
      <form onSubmit={props.handleSubmit}>
        {props.projectType === ProjectType.NewCompany ? (
          <CreateProjectNewCompany
            benchmarkSectors={props.benchmarkSectors}
            circles={props.circles}
            employeeRangeGroup={props.employeeRangeGroup}
            timezones={props.timezones}
            isEngagementScoreProjectType={props.isEngagementScoreProjectType}
          />
        ) : (
          <CreateProjectExistingCompany circles={props.circles} companies={props.companyList} />
        )}
      </form>
    )}
  </ResourceLoader>
);

export const CreateProjectForm = compose(
  connect((state: AdminState): CreateProjectForm.StateProps => {
    const type = Number(formValueSelector(AdminFormNames.CreateProjectType)(state, 'type'));
    const kind = formValueSelector(AdminFormNames.CreateProject)(state, 'kind');
    const timezones = selectTimezoneAutocompleteOptions(state);
    const initialTimezone = timezones.find(({ value }) => value === DEFAULT_TIMEZONE);
    const listResource = selectCompanyListResource(state);

    return {
      listResource,
      benchmarkSectors: selectBenchmarkSectors(state),
      circlesResource: selectCirclesResource(state),
      circles: selectCircles(state),
      companyList: selectCompanyList(state),
      employeeRangeGroup: selectEmployeeRangeGroup(state),
      timezones: timezones,
      isEngagementScoreProjectType: kind === ProjectKind.PROJECT_ENGAGEMENT_SCORE,
      projectType: type,
      initialValues: {
        brandColor: tinycolor(orange[600]).toRgb(),
        timezone: initialTimezone && initialTimezone.value,
      },
      onSubmit: (data: CreateProjectForm.FormData, dispatch: AdminDispatch) => {
        const model = {
          company:
            type === ProjectType.ExistingCompany
              ? data.company
              : {
                  name: data.companyName,
                  brandColor: data.brandColor,
                  circleId: data.circle || selectCircles(state)[0]?.id,
                  timezone: data.timezone,
                  approxEmployeeNumber: data.approxNumberEmployee,
                  sectors: [data.benchmarkSector],
                  settings: {
                    ssoEnabled: false,
                    multiLanguageEnabled: false,
                    enabledLocales: [],
                    enabledDashboardLocales: [],
                    customKioskCodesEnabled: false,
                  },
                },
          kind: data.kind,
        };
        return projectApi.create(model, { logo: data.companyLogo }).then((project) => {
          dispatch(openSnackbar({ message: 'Project created successfully.' }));
          dispatch(pushRoute(AdminRoute.Project.Survey.SETTINGS, { id: project.id }));
        });
      },
    };
  }),
  reduxForm({
    form: AdminFormNames.CreateProject,
  }),
)(CreateProjectFormPure) as React.FC<CreateProjectForm.OwnProps>;
